import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getFeedbackDetail, updateFeedbackStatus } from '../api/admin';
import GradientAnalysisSection from '../components/GradientAnalysisSection';
import SpeedAnalysisSection from '../components/SpeedAnalysisSection';
import WorkoutSummaryStats from '../components/WorkoutSummaryStats';
import WhatIfSummaryBox from '../components/WhatIfSummaryBox';
import WorkoutChartsSection from '../components/WorkoutChartsSection';
import GpxFitTraceabilitySection from '../components/GpxFitTraceabilitySection';

const AdminFeedbackDetail = () => {
  const { feedbackId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [analysisSession, setAnalysisSession] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [supportNotes, setSupportNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [analysisType, setAnalysisType] = useState(0); // 0 for gradient, 1 for speed

  useEffect(() => {
    loadFeedbackDetail();
  }, [feedbackId]);

  const loadFeedbackDetail = async () => {
    try {
      setLoading(true);
      const data = await getFeedbackDetail(feedbackId);
      setFeedback(data.feedback);
      setAnalysisSession(data.analysis_session);
      setNewStatus(data.feedback.status);
      setSupportNotes(data.feedback.support_notes || '');
    } catch (error) {
      console.error('Failed to load feedback detail:', error);
      alert('Failed to load feedback details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      setUpdating(true);
      await updateFeedbackStatus(feedbackId, newStatus, supportNotes);
      alert('Feedback updated successfully');
      loadFeedbackDetail();
    } catch (error) {
      console.error('Failed to update feedback:', error);
      alert('Failed to update feedback status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'error',
      reviewing: 'warning',
      resolved: 'success',
      closed: 'default'
    };
    return colors[status] || 'default';
  };

  // Prepare data (must be before early returns to avoid conditional hooks)
  const workoutData = analysisSession ? {
    ...analysisSession.workout_data,
    analysis_session_id: analysisSession.id,
    fit_filename: analysisSession.fit_filename,
    gpx_filename: analysisSession.gpx_filename,
  } : null;

  const parameters = analysisSession?.parameters;
  const fitChunks = analysisSession?.fit_chunks || [];
  const gpxSegments = analysisSession?.gpx_segments || [];

  // Early returns after all hooks
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!feedback || !analysisSession) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Feedback not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin')}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h5">
            Feedback #{feedback.id}
          </Typography>
          <Chip
            label={feedback.status}
            color={getStatusColor(feedback.status)}
          />
        </Box>
      </Box>

      {/* Feedback Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Feedback Details
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary">User:</Typography>
          <Typography variant="body1">{feedback.user_email}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary">Submitted:</Typography>
          <Typography variant="body1">
            {new Date(feedback.created_at).toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary">Message:</Typography>
          <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
            {feedback.message}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Status Update */}
        <Typography variant="h6" gutterBottom>
          Update Status
        </Typography>
        <Box display="flex" gap={2} alignItems="flex-start">
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="reviewing">Reviewing</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Support Notes"
            multiline
            rows={3}
            fullWidth
            value={supportNotes}
            onChange={(e) => setSupportNotes(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={handleUpdateStatus}
            disabled={updating}
            sx={{ mt: 1 }}
          >
            Update
          </Button>
        </Box>
      </Paper>

      {/* Workout Summary Stats */}
      {workoutData && workoutData.total_distance && (
        <WorkoutSummaryStats workoutData={workoutData} />
      )}

      {/* What-If Summary Box */}
      {workoutData && fitChunks && fitChunks.length > 0 && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gap="20px"
          mb={4}
        >
          <WhatIfSummaryBox chunks={fitChunks} workoutData={workoutData} />
        </Box>
      )}

      {/* Workout Analysis - Categories */}
      {fitChunks && fitChunks.length > 0 && (
        <>
          <Paper
            elevation={3}
            sx={{
              backgroundColor: colors.grey[800],
              borderRadius: "8px",
              mb: 3
            }}
          >
            <Tabs
              value={analysisType}
              onChange={(e, newValue) => setAnalysisType(newValue)}
              sx={{
                '& .MuiTab-root': {
                  color: colors.grey[300],
                  fontSize: '16px',
                  fontWeight: 'bold',
                },
                '& .Mui-selected': {
                  color: colors.blueAccent[500],
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: colors.blueAccent[500],
                },
              }}
            >
              <Tab label={t('gradientAnalysisTab')} />
              <Tab label={t('speedAnalysisTab')} />
            </Tabs>
          </Paper>

          {/* Gradient Analysis */}
          {analysisType === 0 && <GradientAnalysisSection chunks={fitChunks} />}

          {/* Speed Analysis */}
          {analysisType === 1 && <SpeedAnalysisSection chunks={fitChunks} />}
        </>
      )}

      {/* Workout Charts */}
      {fitChunks && fitChunks.length > 0 && (
        <WorkoutChartsSection chunks={fitChunks} workoutData={workoutData} />
      )}

      {/* GPX-FIT Traceability */}
      {gpxSegments && gpxSegments.length > 0 && fitChunks && fitChunks.length > 0 && (
        <GpxFitTraceabilitySection gpxSegments={gpxSegments} fitChunks={fitChunks} />
      )}

      {!fitChunks || fitChunks.length === 0 && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="info">
            No workout analysis data available for this feedback.
          </Alert>
        </Paper>
      )}
    </Container>
  );
};

export default AdminFeedbackDetail;
